import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Printer } from "lucide-react";
import api, { API } from "@/lib/api";
import { gradeLabel } from "@/lib/grade";

const qrSrc = (token) => `${API}/qr/${token}`;
const barcodeSrc = (token) => `${API}/barcode/${token}`;
const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

export const DEFAULT_LABEL_CONFIG = {
  fields: [
    { key: "qr", enabled: true }, { key: "name", enabled: true }, { key: "set", enabled: true },
    { key: "number", enabled: true }, { key: "condition", enabled: true }, { key: "price", enabled: true },
  ],
  width_in: 1.0, height_in: 1.3, columns: 4, offset_top_in: 0, offset_left_in: 0, gap_in: 0.1, roll: false,
};

// Renders a single sticker honoring the saved label config. Reused by the Labels preview.
export function LabelSticker({ card, config }) {
  const conf = config || DEFAULT_LABEL_CONFIG;
  const roll = !!conf.roll;
  const fields = (conf.fields || []).filter((f) => f.enabled);
  const hasQr = fields.some((f) => f.key === "qr");
  const textFields = fields.filter((f) => f.key !== "qr");

  const txt = (key) => {
    switch (key) {
      case "name": return <div key="name" style={{ fontSize: roll ? 7 : 9, fontWeight: 700, lineHeight: 1.05, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: roll ? "nowrap" : "normal", maxWidth: "100%" }}>{card.name}</div>;
      case "set": return card.set_name ? <div key="set" style={{ fontSize: roll ? 6 : 8, color: "#444", lineHeight: 1.05, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", maxWidth: "100%" }}>{card.set_name}</div> : null;
      case "number": return card.card_number ? <div key="number" style={{ fontSize: roll ? 6 : 8, color: "#444", lineHeight: 1.05 }}>#{card.card_number}</div> : null;
      case "condition": return <div key="condition" style={{ fontSize: roll ? 6 : 8, fontWeight: 700, color: "#111" }}>{card.item_type === "slab" ? gradeLabel(card) : card.condition}</div>;
      case "price": return <div key="price" style={{ fontSize: roll ? 8 : 9, fontWeight: 700, color: "#111" }}>{fmt(card.tcg_price)}</div>;
      default: return null;
    }
  };

  if (conf.designer && (conf.layout || []).length) {
    // Free-form designer: absolutely positioned elements (fractions of label size).
    const val = (k) => k === "name" ? card.name : k === "set" ? (card.set_name || "") : k === "number" ? (card.card_number ? `#${card.card_number}` : "") : k === "condition" ? (card.item_type === "slab" ? gradeLabel(card) : (card.condition || "")) : k === "price" ? fmt(card.tcg_price) : "";
    return (
      <div data-testid="qr-sticker" style={{ position: "relative", width: `${conf.width_in}in`, height: `${conf.height_in}in`, overflow: "hidden", background: "#fff", color: "#0b0b0b", boxSizing: "border-box" }}>
        {(conf.layout || []).filter((e) => e.enabled).map((el) => {
          const base = { position: "absolute", left: `${el.x * 100}%`, top: `${el.y * 100}%` };
          if (el.key === "qr") return <img key="qr" src={qrSrc(card.qr_token)} alt="QR" style={{ ...base, width: `${el.w * 100}%`, height: `${el.w * 100}%`, objectFit: "contain" }} />;
          if (el.key === "barcode") return <img key="barcode" src={barcodeSrc(card.qr_token)} alt="Barcode" style={{ ...base, width: `${el.w * 100}%`, height: "auto", objectFit: "contain" }} />;
          return <div key={el.key} style={{ ...base, fontSize: `${el.font}pt`, fontWeight: (el.key === "name" || el.key === "price" || el.key === "condition") ? 700 : 400, lineHeight: 1.05, whiteSpace: "nowrap", maxWidth: `${(1 - el.x) * 100}%`, overflow: "hidden", textOverflow: "ellipsis", textAlign: el.align || "left" }}>{val(el.key)}</div>;
        })}
      </div>
    );
  }

  if (roll) {
    // Thermal roll: compact horizontal layout (QR left, text right) that fits a short label.
    const qpx = Math.round(conf.height_in * 72 * 0.84);
    return (
      <div data-testid="qr-sticker" className="flex flex-row items-center bg-white"
        style={{ width: `${conf.width_in}in`, height: `${conf.height_in}in`, overflow: "hidden", color: "#0b0b0b", padding: "1px 3px", gap: 3, boxSizing: "border-box" }}>
        {hasQr && <img src={qrSrc(card.qr_token)} alt="QR" style={{ width: qpx, height: qpx, flexShrink: 0 }} />}
        <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", textAlign: "left", overflow: "hidden", flex: 1, minWidth: 0 }}>
          {textFields.map((f) => txt(f.key))}
        </div>
      </div>
    );
  }

  // Label sheet: vertical centered layout.
  const qpx = Math.round(Math.min(conf.width_in, conf.height_in) * 72 * 0.72);
  return (
    <div data-testid="qr-sticker" className="flex flex-col items-center justify-center text-center border rounded-md p-1 bg-white"
      style={{ width: `${conf.width_in}in`, minHeight: `${conf.height_in}in`, borderColor: "#e5e7eb", color: "#0b0b0b", breakInside: "avoid" }}>
      {fields.map((f) => f.key === "qr"
        ? <img key="qr" src={qrSrc(card.qr_token)} alt="QR" style={{ width: qpx, height: qpx }} />
        : txt(f.key))}
    </div>
  );
}

export default function QrPrintModal({ open, cards, onClose }) {
  const [config, setConfig] = useState(null);
  useEffect(() => {
    if (open) api.get("/settings").then(({ data }) => setConfig({ ...DEFAULT_LABEL_CONFIG, ...(data.label_config || {}) })).catch(() => setConfig(DEFAULT_LABEL_CONFIG));
  }, [open]);

  if (!open) return null;
  const conf = config || DEFAULT_LABEL_CONFIG;
  // One sticker per physical copy ADDED: prefer print_qty (the amount just added via Add
  // Multiple / Staging commit) over the card's total stock, so a merged card prints only the
  // new copies — not a label for every copy already in inventory.
  const items = (cards || [])
    .filter((c) => c && c.qr_token)
    .flatMap((c) => {
      const qty = Math.max(1, Math.floor(Number(c.print_qty ?? c.quantity) || 1));
      return Array.from({ length: qty }, (_, i) => ({ card: c, k: `${c.id || c.qr_token}-${i}` }));
    });
  const pageCss = conf.roll
    ? `@page { size: ${conf.width_in}in ${conf.height_in}in; margin: 0; }`
    : `@page { margin: 0.4in; }`;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(5,7,12,0.8)", backdropFilter: "blur(4px)" }} data-testid="qr-print-modal">
      <div className="w-full max-w-3xl rounded-2xl border max-h-[92vh] overflow-y-auto" style={{ background: "#0D1017", borderColor: "var(--pk-border)" }}>
        <div className="qr-no-print flex items-center justify-between px-6 py-4 border-b sticky top-0 z-20" style={{ background: "#0D1017", borderColor: "var(--pk-border)" }}>
          <div>
            <h3 className="font-heading text-lg font-bold text-slate-100">Print QR stickers</h3>
            <p className="text-xs text-slate-500">{items.length} sticker{items.length === 1 ? "" : "s"} · {conf.roll ? "thermal roll (one per label)" : "label sheet"} from your Labels settings</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => window.print()} disabled={items.length === 0} data-testid="qr-print-button"
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500 text-[#1a1205] font-semibold text-sm hover:bg-amber-400 disabled:opacity-50">
              <Printer className="h-4 w-4" /> Print
            </button>
            <button onClick={onClose} className="text-slate-500 hover:text-slate-200" data-testid="qr-print-close"><X className="h-5 w-5" /></button>
          </div>
        </div>

        {items.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500">No printable cards (missing QR tokens).</div>
        ) : (
          <div className="p-5">
            <style media="print" dangerouslySetInnerHTML={{ __html: pageCss }} />
            {conf.roll ? (
              <div className="qr-print-sheet" style={{ background: "#fff" }}>
                {items.map((it, i) => (
                  <div key={it.k} style={{ breakAfter: i < items.length - 1 ? "page" : "auto", breakInside: "avoid" }}>
                    <LabelSticker card={it.card} config={conf} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="qr-print-sheet" style={{ display: "grid", gridTemplateColumns: `repeat(${conf.columns}, ${conf.width_in}in)`, gap: `${conf.gap_in}in`, justifyContent: "start", paddingTop: `${conf.offset_top_in}in`, paddingLeft: `${conf.offset_left_in}in` }}>
                {items.map((it) => <LabelSticker key={it.k} card={it.card} config={conf} />)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
